import React from 'react';
import { Copy, Lock } from 'lucide-react';
import Sheet from '../../../common/Sheet';
import { EntryIcon } from './EntryIcon';
import { QUICK_ADD_LABELS, QUICK_ADD_TONES } from './QuickAddMenu';
import { quickAddTypes, isSupervisoryVisit, latestEntryOfType, clockLabel } from '../model/entryMeta';
import { COPY_LAST_TYPES } from '../model/entryPayload';
import { noteTemplateFor } from '../model/noteTemplates';
import type { Entry } from '../types';

export interface AddEntrySheetProps {
  open: boolean;
  onClose: () => void;
  /** Open the form of this entry type */
  onAdd: (type: string) => void;
  /** Open the form of this type prefilled from the latest entry of the type (copy last) */
  onCopyLast: (type: string) => void;
  /** The chart is not a draft (submitted, in QA, complete): nothing can be added */
  locked: boolean;
  discipline?: string | null;
  visitType?: string | null;
  entries: Entry[];
  palliative?: boolean;
}

const COPY_LABELS: Record<string, string> = { vitals: 'vitals', respiratory: 'resp', feed: 'feed', output: 'output' };
const labelFor = (type: string) => QUICK_ADD_LABELS[type] || noteTemplateFor(type)?.label || type;

/** The "Copy last …" tiles offered first: types of this visit that already have an entry */
export const copyLastTiles = (types: string[], entries: Entry[]): { type: string; at: string }[] =>
  (COPY_LAST_TYPES as readonly string[])
    .filter((t) => types.includes(t))
    .map((t) => ({ type: t, last: latestEntryOfType(entries, t) }))
    .filter((t): t is { type: string; last: Entry } => Boolean(t.last))
    .map(({ type, last }) => ({ type, at: clockLabel(last.observedAt || last.date) }));

/**
 * Phase 18U U6: "+ Add" opens this bottom sheet. Tiles are 64 px, three to a row, in the
 * discipline's quick-add order; "Copy last vitals / resp / feed / output" come first when the
 * visit already has one. A chart that is not a draft says why nothing can be added.
 */
const AddEntrySheet: React.FC<AddEntrySheetProps> = ({ open, onClose, onAdd, onCopyLast, locked, discipline, visitType, entries = [], palliative = false }) => {
  if (!open) return null;
  const types = quickAddTypes(discipline, visitType, { palliative });
  const copies = copyLastTiles(types, entries);
  const needsSupervisoryNote = isSupervisoryVisit(visitType) && !entries.some((e) => e.type === 'supervisory' && !e.invalidated);
  const pick = (fn: () => void) => { onClose(); fn(); };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Add to chart"
      subtitle={needsSupervisoryNote ? 'Supervisory visit: add the supervisory note before you sign and submit the chart.' : undefined}
      variant="bottom"
      testId="add-entry-sheet"
    >
      {locked ? (
        <p className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800" data-testid="add-locked">
          <Lock size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
          This chart is submitted. Ask a supervisor to return it to make changes.
        </p>
      ) : (
        <div className="space-y-3">
          {copies.length > 0 && (
            <div className="grid grid-cols-2 gap-2" data-testid="copy-last-tiles">
              {copies.map(({ type, at }) => (
                <button
                  key={`copy-${type}`}
                  type="button"
                  data-copy-type={type}
                  onClick={() => pick(() => onCopyLast(type))}
                  className="flex min-h-[64px] items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-left hover:bg-slate-50"
                >
                  <Copy size={18} className="shrink-0 text-slate-600" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">Copy last {COPY_LABELS[type] || type}</span>
                    {at && <span className="block text-xs text-slate-600">from {at}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2" data-testid="add-entry-grid">
            {types.map((type) => (
              <button
                key={type}
                type="button"
                data-entry-type={type}
                onClick={() => pick(() => onAdd(type))}
                className={`flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl border px-1 text-center ${QUICK_ADD_TONES[type] || QUICK_ADD_TONES.notes}`}
              >
                <EntryIcon type={type} />
                <span className="text-sm font-medium leading-tight">{labelFor(type)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </Sheet>
  );
};

export default AddEntrySheet;
